import { createContext, useContext, useMemo } from "react";

/**
 * FR-015-101, § Overlays: where an interactive floating panel (a popover, a picker, a select, a menu,
 * a hover card) renders. Outside a dialog it is the document body, Radix's default. Inside a dialog it
 * is the dialog's own content: Radix's modal dialog locks scrolling and pointer events to its content,
 * so a panel portalled to the body beside it could not be scrolled with the wheel or a touch, which is
 * Radix's documented reason for the portal's `container`. `Dialog` provides the element; every
 * interactive floating panel reads it through `usePortalContainer`. A tooltip does not: it takes no
 * pointer and scrolls nothing, so that reason does not hold for it, and it always renders in the body
 * on the tooltip layer, where no container can clip it (2026-10-01, `Tooltip.tsx`).
 *
 * Inside a `Popover` it is the popover's own content (2026-10-02, the owner's report that the "⋯" of
 * an offered meeting's popover on the meetings calendar closed everything). A popover is not modal: it
 * closes when the page under it scrolls and when a press lands outside it. A panel opened from inside
 * it (a menu, a select, a picker) is a layer of its own, so it lives inside the popover's element,
 * where the popover's outside-scroll check (`closeOnOutsideScroll.ts`) counts a scroll of that panel's
 * own list as inside rather than as the page moving. A menu opened there is not modal either
 * (`useMenuModal`): a modal menu locks the page's scroll and takes every press outside itself, so a
 * press outside both closed the menu alone, and its scroll lock restyled the body, which Firefox
 * answered with a scroll event on the calendar's scrolled hours that moved nothing, and the popover
 * closed on it, taking the menu with it.
 */
export interface PortalHost {
  /** The element floating panels opened inside the host portal into; null until it mounts, when they portal to the body. */
  readonly element: HTMLElement | null;
  /** Whether the host is modal: a menu opened inside a non-modal host is itself non-modal. */
  readonly modal: boolean;
}

export const PortalContainerContext = createContext<PortalHost | null>(null);

/**
 * The stage every overlay portals into in the document body's place (§ Component gallery,
 * 2026-10-23): the gallery's panel, so a state whose overlay is open, a dialog, a menu, a picker's
 * popover, a tooltip or a toast, is drawn, read for contrast and photographed inside the panel of that
 * state rather than over the page, where the run saw none of it. The panel makes itself the containing
 * block of what is fixed inside it (`contain: layout`), so a dialog's scrim covers the panel and its
 * card centres on it. No screen of the product provides a stage, so there every overlay keeps the
 * document body, as the rules above and `TooltipOverlay`'s say.
 */
export const OverlayStageContext = createContext<HTMLElement | null>(null);

/** The stage an overlay that would portal to the document body portals into, or undefined for the body itself. */
export function useOverlayStage(): HTMLElement | undefined {
  return useContext(OverlayStageContext) ?? undefined;
}

/**
 * The collision boundary a floating panel keeps inside: on a stage, the stage, so a menu or a popover
 * opened on a gallery panel flips and shifts to stay on its own panel rather than over its neighbour;
 * anywhere else nothing, and Radix keeps the panel inside the viewport as it always has.
 */
export function useStageBoundary(): { readonly collisionBoundary?: HTMLElement } {
  const stage = useOverlayStage();
  return stage === undefined ? {} : { collisionBoundary: stage };
}

/** The host a dialog (modal) or a popover (not modal) provides over `element`, its content. */
export function usePortalHost(element: HTMLElement | null, modal: boolean): PortalHost {
  return useMemo(() => ({ element, modal }), [element, modal]);
}

/** The element a floating panel portals into: its host's content, else the stage, else undefined for the document body. */
export function usePortalContainer(): HTMLElement | undefined {
  const host = useContext(PortalContainerContext)?.element ?? undefined;
  const stage = useOverlayStage();
  return host ?? stage;
}

/**
 * Whether a menu opened here is modal, Radix's default for a menu: at the document's root and inside a
 * modal dialog it is; inside a non-modal popover it is not, so the popover's own dismissal (an outside
 * press, `Esc` in it, a scroll of the page) keeps working while the menu is open.
 */
export function useMenuModal(): boolean {
  return useContext(PortalContainerContext)?.modal ?? true;
}
